import {DataLabelPosition} from 'entities/chart';

export function resolveDataLabelPosition(
    chartType: string,
    saved?: DataLabelPosition | null,
): DataLabelPosition {
    if (
        saved === DataLabelPosition.Inside
        || saved === DataLabelPosition.Above
        || saved === DataLabelPosition.Off
    ) {
        return saved;
    }

    if (chartType === 'pie') {
        return DataLabelPosition.Above;
    }

    return DataLabelPosition.Off;
}

export function getDefaultDataLabelPosition(chartType: string): DataLabelPosition {
    return resolveDataLabelPosition(chartType, null);
}

const DEFAULT_MIN_SEGMENT_LABEL_PX = 24;
const COMPACT_MIN_SEGMENT_LABEL_PX = 28;

export function formatDataLabelValue(value: unknown): string {
    const numericValue = Number(value);
    if (value == null || value === '' || Number.isNaN(numericValue)) {
        return '';
    }
    return String(Math.round(numericValue));
}

export function estimatePlotAreaHeight(chartHeight = 1): number {
    return Math.max(80, chartHeight * 70);
}

function getMinSegmentLabelPx(chartHeight = 1, chartWidth = 1): number {
    if (chartHeight === 1 && chartWidth === 1) {
        return Infinity;
    }

    if (chartHeight === 1 || chartWidth === 1) {
        return COMPACT_MIN_SEGMENT_LABEL_PX;
    }

    if (chartHeight <= 2) {
        return 22;
    }

    return DEFAULT_MIN_SEGMENT_LABEL_PX;
}

export function shouldPreferStackTotalOnly(chartHeight = 1, chartWidth = 1): boolean {
    return chartHeight === 1 && chartWidth === 1;
}

export interface StackedLabelContext {
    seriesKeys: string[];
    data: Record<string, unknown>[];
    maxStackTotal: number;
    plotAreaHeight: number;
    perIndexShowSegments: boolean[];
}

export function buildStackedLabelContext(
    seriesKeys: string[],
    data: Record<string, unknown>[],
    chartHeight = 1,
    chartWidth = 1,
): StackedLabelContext {
    const plotAreaHeight = estimatePlotAreaHeight(chartHeight);
    const minSegmentPx = getMinSegmentLabelPx(chartHeight, chartWidth);
    const preferStackTotalOnly = shouldPreferStackTotalOnly(chartHeight, chartWidth);
    const perIndexTotals = data.map((row) =>
        seriesKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0),
    );
    const maxStackTotal = Math.max(0, ...perIndexTotals);
    const perIndexShowSegments = perIndexTotals.map((stackTotal, dataIndex) => {
        if (preferStackTotalOnly || stackTotal <= 0 || maxStackTotal <= 0) {
            return false;
        }

        return seriesKeys.every((key) => {
            const value = Number(data[dataIndex][key]) || 0;
            if (value <= 0) {
                return true;
            }
            const segmentPx = (value / maxStackTotal) * plotAreaHeight;
            return segmentPx >= minSegmentPx;
        });
    });

    return {
        seriesKeys,
        data,
        maxStackTotal,
        plotAreaHeight,
        perIndexShowSegments,
    };
}

function getTopStackSeriesIndex(
    dataIndex: number,
    seriesKeys: string[],
    data: Record<string, unknown>[],
): number {
    for (let index = seriesKeys.length - 1; index >= 0; index -= 1) {
        if ((Number(data[dataIndex][seriesKeys[index]]) || 0) > 0) {
            return index;
        }
    }
    return -1;
}

function createStackedSegmentLabelFormatter(
    context: StackedLabelContext,
    seriesIndex: number,
): (params: {value: unknown; dataIndex: number}) => string {
    return (params) => {
        const formatted = formatDataLabelValue(params.value);
        if (!formatted) {
            return '';
        }

        const {dataIndex} = params;
        if (context.perIndexShowSegments[dataIndex]) {
            return formatted;
        }

        const topIndex = getTopStackSeriesIndex(dataIndex, context.seriesKeys, context.data);
        if (seriesIndex !== topIndex) {
            return '';
        }

        const stackTotal = context.seriesKeys.reduce(
            (sum, key) => sum + (Number(context.data[dataIndex][key]) || 0),
            0,
        );
        return formatDataLabelValue(stackTotal);
    };
}

function createSimpleLabelFormatter(): (params: {value: unknown}) => string {
    return (params) => formatDataLabelValue(params.value);
}

export function getEchartsBarLabelPosition(
    position: DataLabelPosition,
    isHorizontal: boolean,
): string {
    if (position === DataLabelPosition.Inside) {
        return isHorizontal ? 'insideRight' : 'inside';
    }
    return isHorizontal ? 'right' : 'top';
}

export function getEchartsLineLabelPosition(position: DataLabelPosition): string {
    return position === DataLabelPosition.Inside ? 'inside' : 'top';
}

function buildLabelTextStyle(position: DataLabelPosition, useLightText: boolean): Record<string, unknown> {
    if (position === DataLabelPosition.Inside && useLightText) {
        return {
            color: '#ffffff',
            fontWeight: 500,
        };
    }

    return {
        color: '#333333',
        fontWeight: 400,
    };
}

export function buildBarDataLabelConfig(
    dataLabelPosition: DataLabelPosition,
    isHorizontal: boolean,
    options?: {
        stackedContext?: StackedLabelContext | null;
        seriesIndex?: number;
    },
): Record<string, unknown> | undefined {
    if (dataLabelPosition === DataLabelPosition.Off) {
        return undefined;
    }

    const formatter = options?.stackedContext != null && options.seriesIndex != null
        ? createStackedSegmentLabelFormatter(options.stackedContext, options.seriesIndex)
        : createSimpleLabelFormatter();

    return {
        show: true,
        position: getEchartsBarLabelPosition(dataLabelPosition, isHorizontal),
        formatter,
        ...buildLabelTextStyle(dataLabelPosition, dataLabelPosition === DataLabelPosition.Inside),
    };
}

export function buildLineDataLabelConfig(
    dataLabelPosition: DataLabelPosition,
    options?: {
        stackedContext?: StackedLabelContext | null;
        seriesIndex?: number;
    },
): Record<string, unknown> | undefined {
    if (dataLabelPosition === DataLabelPosition.Off) {
        return undefined;
    }

    const formatter = options?.stackedContext != null && options.seriesIndex != null
        ? createStackedSegmentLabelFormatter(options.stackedContext, options.seriesIndex)
        : createSimpleLabelFormatter();

    return {
        show: true,
        position: getEchartsLineLabelPosition(dataLabelPosition),
        formatter,
        ...buildLabelTextStyle(dataLabelPosition, false),
    };
}

export function getLinePointSymbolConfig(dataLabelPosition: DataLabelPosition): {
    showSymbol: boolean;
    symbolSize: number;
    symbol: string;
    itemStyle?: Record<string, unknown>;
} {
    if (dataLabelPosition !== DataLabelPosition.Inside) {
        return {
            showSymbol: false,
            symbolSize: 0,
            symbol: 'circle',
        };
    }

    return {
        showSymbol: true,
        symbolSize: 6,
        symbol: 'circle',
        itemStyle: {
            borderWidth: 1,
            borderColor: '#ffffff',
        },
    };
}

const pieInsideLabelStyle = {
    color: '#ffffff',
    fontWeight: 500,
    textBorderWidth: 1,
    textBorderColor: 'rgba(0, 0, 0, 0.35)',
};

export function pieChartUsesInsideValueOverlay(dataLabelPosition: DataLabelPosition): boolean {
    return dataLabelPosition === DataLabelPosition.Inside;
}

export function buildPieOutsideNameLabelConfig(
    fontSize: number,
    visible: boolean,
): Record<string, unknown> {
    if (!visible) {
        return {show: false};
    }

    return {
        show: true,
        position: 'outside',
        formatter: (params: {name?: string}) => params.name ?? '',
        fontSize,
        color: '#333333',
        fontWeight: 400,
    };
}

export function buildPieOutsideNameAndValueLabelConfig(
    fontSize: number,
    visible: boolean,
): Record<string, unknown> {
    if (!visible) {
        return {show: false};
    }

    return {
        show: true,
        position: 'outside',
        formatter: (params: {name?: string; value?: unknown}) => {
            const name = params.name ?? '';
            const value = formatDataLabelValue(params.value);
            if (!name) {
                return value;
            }
            if (!value) {
                return name;
            }
            return `${name}\n${value}`;
        },
        fontSize,
        color: '#333333',
        fontWeight: 400,
    };
}

export function buildPieInsideValueLabelConfig(
    fontSize: number,
    visible: boolean,
): Record<string, unknown> {
    if (!visible) {
        return {show: false};
    }

    return {
        show: true,
        position: 'inside',
        formatter: (params: {value?: unknown}) => formatDataLabelValue(params.value),
        fontSize,
        ...pieInsideLabelStyle,
    };
}

export function buildPieLabelLineConfig(): Record<string, unknown> {
    return {show: true};
}

export function buildSunburstValueLabelFormatter(
    dataLabelPosition: DataLabelPosition,
    nameFormatter: (params: {name?: string}) => string,
): ((params: {name?: string; value?: unknown}) => string) | undefined {
    if (dataLabelPosition === DataLabelPosition.Off) {
        return nameFormatter;
    }

    if (dataLabelPosition === DataLabelPosition.Above) {
        return (params) => formatDataLabelValue(params.value);
    }

    return (params) => {
        const name = nameFormatter(params);
        const value = formatDataLabelValue(params.value);
        if (!name) {
            return value;
        }
        if (!value) {
            return name;
        }
        return `${name}\n${value}`;
    };
}

export function getSunburstLabelPosition(dataLabelPosition: DataLabelPosition): string | undefined {
    if (dataLabelPosition === DataLabelPosition.Above) {
        return 'outside';
    }
    return undefined;
}

export function appendDataLabelValueToTreemapText(
    text: string,
    value: unknown,
    dataLabelPosition: DataLabelPosition,
): string {
    const formattedValue = formatDataLabelValue(value);
    if (dataLabelPosition === DataLabelPosition.Off || !formattedValue) {
        return text;
    }

    if (dataLabelPosition === DataLabelPosition.Above) {
        return text ? `${formattedValue}\n${text}` : formattedValue;
    }

    return text ? `${text}\n${formattedValue}` : formattedValue;
}

export function appendDataLabelValueToTreemapUpperLabel(
    text: string,
    value: unknown,
    dataLabelPosition: DataLabelPosition,
): string {
    const formattedValue = formatDataLabelValue(value);
    if (dataLabelPosition === DataLabelPosition.Off || !formattedValue || !text) {
        return text;
    }

    if (dataLabelPosition === DataLabelPosition.Above) {
        return `${formattedValue}\n${text}`;
    }

    return `${text} ${formattedValue}`;
}
